import { buildUrlFromLocation } from "@/utils/httpUtils";
import http from "@/services/http";
import { createURI } from "@/utils/string";
import { convertItem } from "@/components/forecasts/model/item";
import { parseDate } from "@/components/forecasts/utils/utilis";
import session from "@/services/session";
import { moment } from "@/utils/moment";

export default {
    async getForecasts() {
        let forecasts = await http.get(buildUrlFromLocation(`/forecast/forecasts`, { campaignId: null }));
        return forecasts.map(fc => this.deserializeForecast(fc));
    },
    async getActiveAmountForecasts() {
        return await http.get(buildUrlFromLocation(`/forecast/forecasts/active-amount`, { campaignId: null }));
    },
    async getActiveAmountForecastWindows() {
        return await http.get(buildUrlFromLocation(`/forecast/zones/windows/active-amount`, { campaignId: null }));
    },
    async getForecast(forecastId, full = true) {
        let url = `/forecast/forecasts/${forecastId}?full=`;
        url += full ? 1 : 0;
        let forecastRes = await http.get(buildUrlFromLocation(url, { campaignId: null }));
        return this.deserializeForecast(forecastRes);
    },
    async updateForecast(forecast) {
        let forecastRes = await http.put(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}`, { campaignId: null }), forecast);
        return this.deserializeForecast(forecastRes);
    },
    async deleteForecast(forecast) {
        return await http.delete(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}`, { campaignId: null }));
    },
    async createForecast(data) {
        let forecastRes = await http.post(buildUrlFromLocation(`/forecast/forecasts`, { campaignId: null }), data);
        return this.deserializeForecast(forecastRes);
    },
    async publishForecast(forecast, onlyAffiliate) {
        let forecastRes = await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/publish`, { campaignId: null }), {
            onlyAffiliate
        });
        return this.deserializeForecast(forecastRes);
    },
    async duplicateForecast(forecast, name, reference) {
        let forecastRes = await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/duplicate`, { campaignId: null }), {
            name,
            reference
        });
        return this.deserializeForecast(forecastRes);
    },
    async getForecastWindow(id, full = false) {
        let url = `/forecast/windows/${id}?full=`;
        url += full ? 1 : 0;
        let res = await http.get(buildUrlFromLocation(url, { campaignId: null }));
        return this.deserializeForecastWindow(res);
    },
    async getForecastWindowExportXLS(id) {
        return await http.get(buildUrlFromLocation(`/forecast/windows/${id}/export-xls`, { campaignId: null }), {
            responseType: "arraybuffer"
        });
    },
    async getForecastWindowExportXLSForImport(id) {
        return await http.get(buildUrlFromLocation(`/forecast/windows/${id}/export-xls/for-import`, { campaignId: null }), {
            responseType: "arraybuffer"
        });
    },
    async getForecastWindowContributors(forecastWindow) {
        return await http.get(buildUrlFromLocation(`/forecast/windows/${forecastWindow.id}/contributors`, { campaignId: null }));
    },
    async getForecastWindowsForByZones() {
        let res = await http.get(buildUrlFromLocation(`/forecast/zones/windows`, { campaignId: null }));
        return res.map(fcw => this.deserializeForecastWindow(fcw));
    },
    async getForecastWindowsForForecast(forecastId) {
        let res = await http.get(buildUrlFromLocation(`/forecast/forecasts/${forecastId}/windows`, { campaignId: null }));
        return res.map(fcw => this.deserializeForecastWindow(fcw));
    },
    async updateForecastWindow(forecastWindow) {
        const godMode = this.isGodMode();
        let res = await http.post(
            buildUrlFromLocation(`/forecast/windows/${forecastWindow.id}?godMode=${godMode}`, { campaignId: null }),
            forecastWindow
        );
        return this.deserializeForecastWindow(res);
    },
    async validateForecastWindow(forecastWindow) {
        let res = await http.post(buildUrlFromLocation(`/forecast/windows/${forecastWindow.id}/validate`, { campaignId: null }), forecastWindow);
        return this.deserializeForecastWindow(res);
    },
    async updateForecastSecondaryWindow(forecastWindow) {
        return await http.post(buildUrlFromLocation(`/forecast/windows/secondary/${forecastWindow.id}`, { campaignId: null }), forecastWindow);
    },
    async getCampaigns() {
        let campaigns = await http.get(buildUrlFromLocation(`/forecast/get-campaigns`, { campaignId: null }));
        return campaigns.map(campaign => {
            return this.deserializeCampaign(campaign);
        });
    },
    async getRequiredActions() {
        return await http.get(buildUrlFromLocation(`/forecast/required-actions`, { campaignId: null }));
    },
    async getPathForExportXLS(forecastIds) {
        return await http.get(buildUrlFromLocation(`/forecast/export-excel?ids=${JSON.stringify(forecastIds)}`, { campaignId: null }), {
            responseType: "arraybuffer"
        });
    },
    async getUserBrandZones(all = false) {
        let url = `/forecast/zones-user`;
        return await http.get(buildUrlFromLocation(url, { campaignId: null }));
    },
    async getBrandZones(all = false) {
        let url = `/forecast/zones`;
        if (all) {
            url = url + "?all=1";
        }
        return await http.get(buildUrlFromLocation(url, { campaignId: null }));
    },
    async getAdminBrandZones(app = null, withDeleted = false, group = null) {
        let appUrl = app ? "/" + app : "";
        let url = `${appUrl}/admin/brand-zones?withDeleted=${withDeleted}`;
        if (group) {
            url = url + "?group=" + group;
        }
        return await http.get(buildUrlFromLocation(url, { campaignId: null }));
    },
    async getAdminBrandZone(zoneId) {
        let url = `/admin/brand-zones/zone/${zoneId}`;
        return await http.get(buildUrlFromLocation(url, { campaignId: null }));
    },
    async getItems(forecastId, full = false) {
        let url = `/forecast/forecasts/${forecastId}/items`;
        if (full) {
            url = url + "?full=1";
        }
        return await http.get(buildUrlFromLocation(url, { campaignId: null }));
    },
    getItemsFwProperties: async function(forecastId, fwId = null) {
        let url = `/forecast/forecast-window-item-properties/${forecastId}`;
        if (fwId != null) {
            return await http.get(buildUrlFromLocation(url + "?fwId=" + fwId, { campaignId: null }));
        } else {
            return await http.get(buildUrlFromLocation(url, { campaignId: null }));
        }
    },
    async updateItemsFwProperties(forecast, items) {
        let url = `/forecast/forecast-window-item-properties/${forecast.id}/items/order`;
        return await http.post(buildUrlFromLocation(url, { campaignId: null }), { items });
    },
    async getPrices(item) {
        let url = `/forecast/items/${item.id}/prices`;
        return await http.get(buildUrlFromLocation(url, { campaignId: null }));
    },
    async updateItems(forecast, itemsToAdd = [], itemsToRemove = []) {
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/items/update`, { campaignId: null }), {
            add: this.serializeItems(itemsToAdd),
            remove: this.serializeItems(itemsToRemove)
        });
    },
    async updateItemsFromImport(forecast, items) {
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/items-from-import`, { campaignId: null }), {
            items
        });
    },
    async uploadXls(forecast, file) {
        let formData = new FormData();
        formData.append("file", file);
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/items/xls`, { campaignId: null }), formData, {
            "Content-Type": "multipart/form-data"
        });
    },
    async updateItemsOrder(forecast, itemAndPosition) {
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/items/order`, { campaignId: null }), itemAndPosition);
    },
    async updateItemsOrderMasse(forecast, items) {
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/items/order-masse`, { campaignId: null }), { items });
    },
    async updateItemsColor(forecast, itemsId, color) {
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/items/color`, { campaignId: null }), { itemsId, color });
    },
    async updateItemsColorFw(forecast, itemsId, color) {
        return await http.post(buildUrlFromLocation(`/forecast/forecast-window-item-properties/${forecast.id}/items/color`, { campaignId: null }), {
            itemsId,
            color
        });
    },
    async searchItems({ searchTerm, limit, page, status }) {
        let uri = createURI("/components", {
            query: {
                // systemType: systemTypeId,
                search: searchTerm,
                limit,
                offset: page,
                status
            }
        });
        let response = await http.get(buildUrlFromLocation(uri, { campaignId: null }));
        response.data = await this.deserializeItems(response.data);
        return response;
    },
    async reChallengeForecastWindows({ forecast, forecastWindows, message }) {
        let payload = {
            forecastWindowsIds: forecastWindows.map(fw => fw.id),
            message: message
        };
        return await http.post(buildUrlFromLocation(`/forecast/forecasts/${forecast.id}/re-challenge`, { campaignId: null }), payload);
    },

    async approveTransmission(forecastId) {
        return await http.put(buildUrlFromLocation(`/forecast/forecasts/${forecastId}/approve-transmission`, { campaignId: null }));
    },
    deserializeForecast(forecast) {
        forecast.startDate = moment(forecast.startDate).toDate();
        forecast.endDate = moment(forecast.endDate).toDate();
        forecast.refDate = moment(forecast.refDate).toDate();
        forecast.launchDate = forecast.launchDate ? moment(forecast.launchDate).toDate() : null;
        forecast.availabilityDate = forecast.availabilityDate ? moment(forecast.availabilityDate).toDate() : null;
        forecast.endOfCampaign = forecast.endOfCampaign ? moment(forecast.endOfCampaign).toDate() : null;
        forecast.preseriesDate = forecast.preseriesDate ? moment(forecast.preseriesDate).toDate() : null;

        if (forecast.datesRecurrence && forecast.datesRecurrence.length) {
            for (let i = 0; i < forecast.datesRecurrence.length; i++) {
                forecast.datesRecurrence[i] = parseDate(forecast.datesRecurrence[i]);
            }
        }

        if (forecast.hasOwnProperty("campaign")) {
            forecast.campaign = this.deserializeCampaign(forecast.campaign);
        }
        return forecast;
    },
    async deserializeItems(items, withPrices = true) {
        return Promise.all(
            items.map(item => {
                if (withPrices) {
                    return new Promise(resolve => {
                        this.getPrices(item).then(res => {
                            item.prices = res;
                            item = convertItem(item);
                            resolve(item);
                        });
                    });
                } else {
                    return convertItem(item);
                }
            })
        );
    },
    serializeItems(items) {
        return items.map(item => {
            return { id: item.id };
        });
    },
    deserializeForecastWindow(forecastWindow) {
        forecastWindow.isValidated = false;
        if (forecastWindow.startDate) {
            forecastWindow.startDate = parseDate(forecastWindow.startDate);
        }
        if (forecastWindow.endDate) {
            forecastWindow.endDate = parseDate(forecastWindow.endDate);
        }
        if (forecastWindow.validatedDate) {
            forecastWindow.validatedDate = parseDate(forecastWindow.validatedDate);
            forecastWindow.isValidated = true;
        }
        forecastWindow.forecast = this.deserializeForecast(forecastWindow.forecast);
        return forecastWindow;
    },
    deserializeCampaign(campaign) {
        return {
            id: campaign.id,
            name: campaign.name,
            refClient: campaign.refClient,
            // launchDate: parseDate(campaign.launchDate),
            launchDate: this.parseDate(campaign.launchDate, true),
            // availabilityDate: parseDate(campaign.stockAvailability),
            availabilityDate: this.parseDate(campaign.stockAvailability, true),
            endDate: this.parseDate(campaign.endDate, true),
            preseriesDate: this.parseDate(campaign.preseriesDate, true)
        };
    },
    isGodMode() {
        const isGod = JSON.parse(session.getValueSession("godMode"));
        return isGod != null ? isGod : false;
    },
    // Retourne un object Date si la date existe / est valide, null sinon
    // un string représentant une date (ex : 2022-01-01)
    parseDate(date) {
        if (date === null) {
            return null;
        }

        return moment(date, true).toDate();
    }
};



// WEBPACK FOOTER //
// ./src/components/forecasts/forecastsService.js